import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './field.module.css';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
}

/**
 * Campo de formulario accesible: la etiqueta, el error y la ayuda quedan
 * asociados al input por id, de modo que un lector de pantalla los anuncia.
 */
export function Field({ label, error, hint, ...rest }: Readonly<FieldProps>) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error === undefined ? '' : errorId, hint === undefined ? '' : hintId]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.group}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <input
        {...rest}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        aria-invalid={error !== undefined}
        className={styles.input}
        id={id}
      />
      {hint === undefined ? null : (
        <span className={styles.hint} id={hintId}>
          {hint}
        </span>
      )}
      {error === undefined ? null : (
        <span className={styles.error} id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
